import React, { useMemo, useState, useEffect, useRef } from 'react';
import { DualAxes } from '@ant-design/plots';
import dayjs from 'dayjs';

export interface ComboPoint {
  date: number;
  [key: string]: number;
}

interface SeriesConfigItem {
  showName?: string;
  kind?: 'line' | 'column';
  color?: string;
  aggregate?: 'sum' | 'avg';
  yAxis?: string;
}

interface Props {
  data: Array<{ date: number } & Record<string, any>>;
  title?: string;
  seriesCfg?: Record<string, SeriesConfigItem>;
}

const defaultPalette = ['#5B8FF9', '#5AD8A6', '#5D7092', '#F6BD16', '#6F5EF9', '#7262FD', '#78D3F8', '#FF9D4D', '#FF6F61', '#6DC8EC', '#BEE3DB'];

const transformData = (
  data: Array<{ date: number } & Record<string, any>>,
  seriesCfg?: Record<string, SeriesConfigItem>
) => {
  if (!data || data.length === 0) return [] as { date: number; time: string; values: Record<string, number> }[];
  
  const sorted = [...data].sort((a, b) => a.date - b.date);
  
  const keysFromData = Array.from(
    new Set(
      (data || []).flatMap(d => Object.keys(d).filter(k => k !== 'date'))
    )
  );
  const seriesKeys = Object.keys(seriesCfg || {}).length > 0 ? Object.keys(seriesCfg || {}) : keysFromData;

  return sorted.map(d => {
    const time = dayjs(d.date).format('YYYY-MM-DD');
    const values: Record<string, number> = {};
    seriesKeys.forEach(s => {
      if (typeof d[s] === 'number') values[s] = d[s];
    });
    return { date: d.date, time, values };
  });
};

const buildDualAxesConfig = (
  aggData: { date: number; time: string; values: Record<string, number> }[],
  seriesCfg?: Record<string, SeriesConfigItem>,
  availableWidth?: number
) => {
  const keysFromData = Array.from(
    new Set(
      aggData.flatMap(d => Object.keys(d.values || {}))
    )
  );
  const seriesKeys = Object.keys(seriesCfg || {}).length > 0 ? Object.keys(seriesCfg || {}) : keysFromData;
  const colorMap: Record<string, string> = {};
  seriesKeys.forEach((s, i) => { colorMap[s] = seriesCfg?.[s]?.color || defaultPalette[i % defaultPalette.length]; });
  const times = aggData.map(d => d.time);
  const itemWidth = 60;
  const minContentWidth = Math.max(times.length * itemWidth, 600);
  const effectiveAvailableWidth = availableWidth || (typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isOverflow = minContentWidth > effectiveAvailableWidth;
  const containerWidth = isOverflow ? minContentWidth : effectiveAvailableWidth;
  const modeKey = isOverflow ? `fixed-${containerWidth}` : `auto-${times.length}-${effectiveAvailableWidth}`;
  const combinedData = aggData.map(d => {
    const rec: any = { time: d.time };
    seriesKeys.forEach(s => { if (typeof d.values[s] === 'number') rec[s] = d.values[s]; });
    return rec;
  });
  const rawAxisKeys = Array.from(new Set(seriesKeys.map(s => seriesCfg?.[s]?.yAxis).filter(Boolean))) as string[];
  const axisKeys = rawAxisKeys.length ? (rawAxisKeys.includes('left') ? ['left', ...rawAxisKeys.filter(k => k !== 'left')] : rawAxisKeys) : ['left'];
  const axisPosMap: Record<string, 'left' | 'right'> = {};
  axisKeys.forEach(k => { axisPosMap[k] = k === 'right' ? 'right' : 'left'; });
  const axisSeriesMap: Record<string, string[]> = {};
  axisKeys.forEach(k => { axisSeriesMap[k] = seriesKeys.filter(s => (seriesCfg?.[s]?.yAxis || axisKeys[0]) === k); });
  const maxByAxis: Record<string, number> = {};
  axisKeys.forEach(k => { maxByAxis[k] = Math.max(0, ...aggData.map(d => Math.max(0, ...axisSeriesMap[k].map(s => d.values[s] || 0)))); });
  const children: any[] = [];
  const lineChildren: any[] = [];
  const independent = axisKeys.length > 1;
  const axisScales: Record<string, any> = {};
  axisKeys.forEach(k => { const keyName = independent ? `y_${k}` : 'yShared'; axisScales[k] = { y: { key: keyName, independent, domain: [0, maxByAxis[k] > 0 ? maxByAxis[k] * 1.1 : 10] } }; });
  seriesKeys.forEach(s => {
    const isLine = (seriesCfg?.[s]?.kind || 'column') === 'line';
    if (isLine) {
      const axisK = (seriesCfg?.[s]?.yAxis || axisKeys[0]);
      const pos = axisPosMap[axisK];
      const base: any = { yField: s, scale: axisScales[axisK], colorField: colorMap[s] };
      lineChildren.push({ type: 'line', ...base, shapeField: 'smooth', style: { lineWidth: 3 }, axis: pos === 'right' ? { y: { position: 'right' } } : undefined });
    }
  });

  const columnsByAxis: Record<string, string[]> = {};
  axisKeys.forEach(k => { columnsByAxis[k] = axisSeriesMap[k].filter(s => (seriesCfg?.[s]?.kind || 'column') === 'column'); });
  const longByAxis: Record<string, any[]> = {};
  axisKeys.forEach(k => { longByAxis[k] = []; });
  aggData.forEach(d => {
    axisKeys.forEach(k => {
      columnsByAxis[k].forEach(s => {
        const v = d.values[s];
        if (typeof v === 'number') longByAxis[k].push({ time: d.time, type: seriesCfg?.[s]?.showName || s, value: v });
      });
    });
  });
  axisKeys.forEach(k => {
    const long = longByAxis[k];
    if (long.length) {
      const colorMapByType: Record<string, string> = {};
      columnsByAxis[k].forEach(s => { colorMapByType[seriesCfg?.[s]?.showName || s] = colorMap[s]; });
      const axis = axisPosMap[k] === 'right' ? { y: { position: 'right' } } : undefined;
      const typeNames = columnsByAxis[k].map(s => (seriesCfg?.[s]?.showName || s));
      const scale = { ...axisScales[k], color: { domain: typeNames, range: typeNames.map(n => colorMapByType[n]) } };
      children.push({ type: 'interval', data: long, xField: 'time', yField: 'value', colorField: 'type', group: true, transform: [{ type: 'dodgeX', padding: 0.2 }], scale, axis, style: { radius: 4, maxWidth: 80, fillOpacity: 1 }, tooltip: lineChildren.length > 0 ? false: undefined });
    }
  });
  children.push(...lineChildren);
  const config = {
    data: combinedData,
    xField: 'time',
    legend: false,
    children,
    tooltip: {
      shared: true,
      title: 'time',
      items: lineChildren.length > 0
        ? seriesKeys.map(s => (
            { name: seriesCfg?.[s]?.showName || s, color: colorMap[s], channel: 'y', field: s }
          ))
        : undefined,
    },
    interaction: { tooltip:  lineChildren.length > 0 ?  { series: true } : { shared: true, series: false } },
    autoFit: !isOverflow,
    width: isOverflow ? containerWidth : undefined,
  } as any;
  const showNameMap: Record<string, string> = {};
  seriesKeys.forEach(s => { showNameMap[s] = seriesCfg?.[s]?.showName || s; });
  return { config, meta: { seriesKeys, colorMap, times, containerWidth, showNameMap, modeKey, isOverflow } };
};

const TrendComboChart: React.FC<Props> = ({ data, title, seriesCfg }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const aggData = useMemo(() => transformData(data, seriesCfg), [data, seriesCfg]);
  const effectiveWidth = containerWidth || (typeof window !== 'undefined' ? window.innerWidth : 0);
  const { config, meta } = useMemo(() => buildDualAxesConfig(aggData as any, seriesCfg, effectiveWidth), [aggData, seriesCfg, effectiveWidth]);
  if (!data || data.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>暂无数据</div>;
  }
  return (
    <div style={{ width: '100%' }} ref={containerRef}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ flex: 1, fontWeight: 500, fontSize: 16 }}>{title}</div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 16, fontSize: 12, flexWrap: 'wrap' }}>
          {meta.seriesKeys.map(key => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: ((seriesCfg?.[key]?.kind || 'column') === 'line' ? 12 : 8), height: ((seriesCfg?.[key]?.kind || 'column') === 'line' ? 2 : 8), borderRadius: ((seriesCfg?.[key]?.kind || 'column') === 'line' ? 0 : 2), backgroundColor: meta.colorMap[key] }} />
              <span style={{ color: '#666' }}>{meta.showNameMap[key]}</span>
            </div>
          ))}
        </div>
        <div style={{ flex: 1 }} />
      </div>
      <div style={{ width: '100%', overflowX: (meta.isOverflow ? 'auto' : 'hidden'), overflowY: 'hidden', paddingBottom: 10 }}>
        {config.children && config.children.length > 0 && <DualAxes key={meta.modeKey} {...config} />}
      </div>
    </div>
  );
};

export default TrendComboChart;
